import { Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectorRef, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { PartnershipSetupService } from './partnership-setup.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  generalConfigFromGlobal,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-partnership-setup',
    templateUrl: './partnership-setup.component.html',
    styleUrls: ['./partnership-setup.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PartnershipSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // ========== STANDARD COMPONENT PROPERTIES ==========
  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // ========== INPUT DATA ==========
  rowData: any = {};
  headerData: any = {};
  filterParams: any = {};
  baseUrl = '';
  globalService: any = {};

  // ========== INITIALIZATION GATE ==========
  isInitialized = false;
  isLoading = false;

  // ========== ENTITY DISPLAY ==========
  entityList: any[] = [];
  modalTitle = '';

  // ========== FORM DATA ==========
  PSHIP_LEID = '';
  PSHIP_TAX_CODE = '';
  PSHIP_REPORTING_PERIOD = '';
  PSHIP_NAME = '';
  PSHIP_US_TAX_TYPE = '';
  PSHIP_BUSINESS = '';
  PSHIP_CURRENCY = '';
  PSHIP_COMPANY_CODE = '';
  PSHIP_HO_LEID = '';
  PSHIP_HO_TAX_CODE = '';
  PSHIP_HO_REPORTING_PERIOD = '';
  PSHIP_HO_NAME = '';
  PSHIP_HO_COMPANY_CODE = '';
  PSHIP_HO_US_TAX_TYPE = '';
  PSHIP_BEGIN_DATE = '';
  PSHIP_END_DATE = '';
  PSHIP_INTERNAL_OR_EXTERNAL_INDICATOR = '';
  PSHIP_COMBINATION_KEY: any = null;

  isLocked = false;
  syncPosting = '0';
  partnerList: any[] = [];
  partnerListOrig: any[] = [];
  postingComboList: any[] = [];
  postingLOVArray: any[][] = [];
  partnerLEList: string[] = [];

  totalEffectiveOwnershipPercentage = 0;
  totalAllocationPercentage = 0;

  validationErrMsg: string | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private partnershipSetupService: PartnershipSetupService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const config = generalConfigFromGlobal(this.gtwGlobal);
    this.baseUrl = config.base_url || config.api || '';
    this.headerData = header;
    this.globalService = {
      globalParams: header.globalParams || {},
      userSettings: header.userSettingsData || {}
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.isLocked = (this.filterParams?.is_locked || this.globalService?.globalParams?.is_locked || 'N') === 'Y';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.baseUrl || !this.globalService?.globalParams || !this.rowData?.COMBINATION_KEY || !this.filterParams?.tax_year) return;

    this.isLoading = true;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.setupEntityData();
    this.showFormLoader = true;
    this.loadFormData();
  }

  private setupEntityData(): void {
    this.PSHIP_LEID = this.rowData.PSHIP_LEID;
    this.PSHIP_TAX_CODE = this.rowData.PSHIP_TAX_CODE;
    this.PSHIP_REPORTING_PERIOD = this.rowData.PSHIP_REPORTING_PERIOD;
    this.PSHIP_NAME = this.rowData.PSHIP_NAME;
    this.PSHIP_US_TAX_TYPE = this.rowData.PSHIP_US_TAX_TYPE;
    this.PSHIP_BUSINESS = this.rowData.PSHIP_BUSINESS;
    this.PSHIP_CURRENCY = this.rowData.PSHIP_CURRENCY;
    this.PSHIP_COMPANY_CODE = this.rowData.PSHIP_COMPANY_CODE;
    this.PSHIP_HO_LEID = this.rowData.PSHIP_HO_LEID;
    this.PSHIP_HO_TAX_CODE = this.rowData.PSHIP_HO_TAX_CODE;
    this.PSHIP_HO_REPORTING_PERIOD = this.rowData.PSHIP_HO_REPORTING_PERIOD;
    this.PSHIP_HO_NAME = this.rowData.PSHIP_HO_NAME;
    this.PSHIP_HO_COMPANY_CODE = this.rowData.PSHIP_HO_COMPANY_CODE;
    this.PSHIP_HO_US_TAX_TYPE = this.rowData.PSHIP_HO_US_TAX_TYPE;
    this.PSHIP_BEGIN_DATE = this.rowData.PSHIP_BEGIN_DATE;
    this.PSHIP_END_DATE = this.rowData.PSHIP_END_DATE;
    this.PSHIP_INTERNAL_OR_EXTERNAL_INDICATOR = this.rowData.PSHIP_INTERNAL_OR_EXTERNAL_INDICATOR;
    this.PSHIP_COMBINATION_KEY = this.rowData.COMBINATION_KEY;

    const taxYear = this.filterParams.tax_year || this.rowData.TAX_YEAR || '';
    const scenarioLabel = this.filterParams.scenario_label || this.filterParams.scenarioLabel || this.globalService?.globalParams?.scenario_desc || '';
    this.modalTitle = `Partnership/Partner Setup Details ( Tax Year: ${taxYear} - Scenario: ${scenarioLabel} )`;

    this.entityList = [
      { key: 'Tax Year', value: taxYear },
      { key: 'LEID', value: this.rowData.PSHIP_LEID },
      { key: 'LE Name', value: this.rowData.PSHIP_NAME }
    ];
  }

  private loadFormData(): void {
    this.showFormLoader = true;

    this.partnershipSetupService.loadPartnerList(
      this.baseUrl, this.filterParams, this.globalService, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.partnerList = data.jsonObject || [];
        this.partnerListOrig = JSON.parse(JSON.stringify(this.partnerList));

        this.totalEffectiveOwnershipPercentage = 0;
        this.totalAllocationPercentage = 0;
        this.partnerLEList = [];

        for (const partner of this.partnerList) {
          this.totalEffectiveOwnershipPercentage += (partner.EFFECTIVE_OWNERSHIP_PERCENTAGE || 0);
          this.totalAllocationPercentage += (partner.ALLOCATION_PERCENTAGE || 0);
          this.partnerLEList.push(
            `${partner.PARTNER_LEID}/${partner.PARTNER_TAX_CODE}/${partner.PARTNER_REPORTING_PERIOD}`
          );
        }

        if (!this.isValidNumber(this.totalEffectiveOwnershipPercentage)) {
          this.totalEffectiveOwnershipPercentage = 0;
        }
        if (!this.isValidNumber(this.totalAllocationPercentage)) {
          this.totalAllocationPercentage = 0;
        }
        this.totalAllocationPercentage = this.roundToSix(this.totalAllocationPercentage);

        this.loadPostingCombos();
      },
      (error: any) => {
        console.error('Error loading partner list:', error);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadPostingCombos(): void {
    this.partnershipSetupService.loadPostingCombos(
      this.baseUrl, this.filterParams, this.globalService, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (postingData: any) => {
        this.postingComboList = postingData.jsonObject || [];
        this.postingLOVArray = [];

        for (const partnerLE of this.partnerLEList) {
          const lovForPartner = this.postingComboList.filter(
            (combo: any) => combo.PTR_LE === partnerLE
          );
          this.postingLOVArray.push(lovForPartner);
        }

        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading posting combos:', error);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  setPosting(ptr: any, index: number): void {
    if (!ptr.POSTING_COMBO) {
      return;
    }

    const matchedCombo = this.postingComboList.find(
      (combo: any) =>
        combo.PTR_LE === `${ptr.PARTNER_LEID}/${ptr.PARTNER_TAX_CODE}/${ptr.PARTNER_REPORTING_PERIOD}` &&
        combo.POSTING_COMBO === ptr.POSTING_COMBO
    );

    if (matchedCombo) {
      ptr.PTR_COMBINATION_KEY = matchedCombo.COMBINATION_KEY;

      const postingAttr = ptr.POSTING_COMBO.split('/');
      ptr.POSTING_PARTNER_LEID = postingAttr[0];
      ptr.POSTING_PARTNER_TAX_CODE = postingAttr[1];
      ptr.POSTING_PARTNER_REPORTING_PERIOD = postingAttr[2];
      ptr.POSTING_PARTNER_BUSINESS = postingAttr[3];
      ptr.POSTING_PARTNER_COMPANY_CODE = postingAttr[1];
      ptr.POSTING_PARTNER_CURRENCY = postingAttr[4];

      const nameMatch = this.postingComboList.find(
        (combo: any) => combo.POSTING_COMBO === ptr.POSTING_COMBO
      );
      if (nameMatch) {
        ptr.POSTING_PARTNER_NAME = nameMatch.POSTING_LE_NAME;
      }
    }

    this.adjustAllocPercentage(ptr);
  }

  adjustAllocPercentage(ptr: any): void {
    this.totalAllocationPercentage = 0;

    for (const partner of this.partnerList) {
      if (partner.ALLOCATION_PERCENTAGE === undefined || partner.ALLOCATION_PERCENTAGE === null) {
        partner.ALLOCATION_PERCENTAGE = 0;
      }
      this.totalAllocationPercentage += partner.ALLOCATION_PERCENTAGE;
    }

    this.totalAllocationPercentage = this.roundToSix(this.totalAllocationPercentage);
  }

  save(): void {
    this.validationErrMsg = null;
    const isValid = this.validateSetupData();

    if (!isValid) {
      this._snackBar.open(this.validationErrMsg || 'Validation failed', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const returnClobSettingsObj: any = {};
    returnClobSettingsObj['tax_year'] = this.filterParams.tax_year;
    returnClobSettingsObj['scenario'] = this.filterParams.scenario;
    returnClobSettingsObj['jcd_key'] = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key;
    returnClobSettingsObj['sso_id'] = this.globalService?.userSettings?.user?.sso_id || '';
    returnClobSettingsObj['pship_cc_key'] = this.PSHIP_COMBINATION_KEY;
    returnClobSettingsObj['sync_posting'] = this.syncPosting;

    const pshipSetupSaveDtls: any[] = [];

    for (let i = 0; i < this.partnerList.length; i++) {
      const partner = this.partnerList[i];
      const origPartner = this.partnerListOrig[i];
      const rec: any = {};

      rec['leid'] = this.PSHIP_LEID;
      rec['cdr_no'] = this.PSHIP_TAX_CODE;
      rec['reporting_period'] = this.PSHIP_REPORTING_PERIOD;
      rec['me_code'] = this.PSHIP_BUSINESS;
      rec['curr'] = this.PSHIP_CURRENCY;
      rec['pship_cc_key'] = this.PSHIP_COMBINATION_KEY;
      rec['pship_int_ext'] = this.PSHIP_INTERNAL_OR_EXTERNAL_INDICATOR;
      rec['pship_ptr_key'] = partner.PSHIP_PTR_KEY;

      const oldPtrPosting = origPartner.POSTING_COMBO;
      if (oldPtrPosting != null) {
        rec['old_ptr_cc_key'] = origPartner.PTR_COMBINATION_KEY;
        const oldPostingAttr = oldPtrPosting.split('/');
        rec['old_ptr_posting_leid'] = oldPostingAttr[0];
        rec['old_ptr_posting_cdr_no'] = oldPostingAttr[1];
        rec['old_ptr_posting_reporting_period'] = oldPostingAttr[2];
        rec['old_ptr_posting_me_code'] = oldPostingAttr[3];
        rec['old_ptr_posting_curr'] = oldPostingAttr[4];
        rec['old_is_inactive'] = origPartner.IS_INACTIVE;
        rec['old_ptr_k1_initial'] = origPartner.PTR_K1_INITIAL;
        rec['old_ptr_k1_final'] = origPartner.PTR_K1_FINAL;
      } else {
        rec['old_ptr_cc_key'] = null;
        rec['old_ptr_posting_leid'] = null;
        rec['old_ptr_posting_cdr_no'] = null;
        rec['old_ptr_posting_reporting_period'] = null;
        rec['old_ptr_posting_me_code'] = null;
        rec['old_ptr_posting_curr'] = null;
        rec['old_is_inactive'] = null;
        rec['old_ptr_k1_initial'] = origPartner.PTR_K1_INITIAL;
        rec['old_ptr_k1_final'] = origPartner.PTR_K1_FINAL;
      }

      const newPtrPosting = partner.POSTING_COMBO;
      if (newPtrPosting != null) {
        const newPostingAttr = newPtrPosting.split('/');
        rec['new_ptr_cc_key'] = partner.PTR_COMBINATION_KEY;
        rec['new_ptr_posting_leid'] = newPostingAttr[0];
        rec['new_ptr_posting_cdr_no'] = newPostingAttr[1];
        rec['new_ptr_posting_reporting_period'] = newPostingAttr[2];
        rec['new_ptr_posting_me_code'] = newPostingAttr[3];
        rec['new_ptr_posting_curr'] = newPostingAttr[4];
      } else {
        rec['new_ptr_cc_key'] = null;
        rec['new_ptr_posting_leid'] = null;
        rec['new_ptr_posting_cdr_no'] = null;
        rec['new_ptr_posting_reporting_period'] = null;
        rec['new_ptr_posting_me_code'] = null;
        rec['new_ptr_posting_curr'] = null;
      }

      rec['new_is_inactive'] = partner.IS_INACTIVE;
      rec['new_ptr_k1_initial'] = partner.PTR_K1_INITIAL;
      rec['new_ptr_k1_final'] = partner.PTR_K1_FINAL;
      rec['posting_alloc'] = (partner.ALLOCATION_PERCENTAGE || 0) / 100;

      pshipSetupSaveDtls.push(rec);
    }

    this.partnershipSetupService.savePartnershipSetupForm(
      this.baseUrl,
      returnClobSettingsObj,
      pshipSetupSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result.data && result.data.errorMessage && result.data.errorMessage !== 'null') {
          this._snackBar.open(result.data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Partner Setup changes saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  private validateSetupData(): boolean {
    if (this.PSHIP_INTERNAL_OR_EXTERNAL_INDICATOR === 'External') {
      return true;
    }

    for (const partner of this.partnerList) {
      const allocPer = partner.ALLOCATION_PERCENTAGE;

      if (allocPer < 0) {
        this.validationErrMsg = 'Partner allocation percentage cannot be less than zero';
        return false;
      }

      if (allocPer > 100) {
        this.validationErrMsg = 'Partner allocation percentage cannot be more than 100';
        return false;
      }

      if (allocPer != null && partner.POSTING_COMBO === undefined) {
        this.validationErrMsg = 'Posting Partner Combo must be selected for non-zero Allocation %';
        return false;
      }
    }

    if (this.totalAllocationPercentage > 100) {
      this.validationErrMsg = 'The sum of allocation percentage must be equal or less than 100';
      return false;
    }

    if (this.PSHIP_INTERNAL_OR_EXTERNAL_INDICATOR === 'Internal') {
      if (this.totalAllocationPercentage !== 100) {
        this.validationErrMsg = 'For Internal Partnership, the sum of allocation percentage must be equal to 100';
        return false;
      }
    }

    return true;
  }

  reset(): void {
    this.partnerList = JSON.parse(JSON.stringify(this.partnerListOrig));
    this.totalAllocationPercentage = 0;
    for (const partner of this.partnerList) {
      this.totalAllocationPercentage += (partner.ALLOCATION_PERCENTAGE || 0);
    }
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private isValidNumber(value: any): boolean {
    return typeof value === 'number' && !isNaN(value);
  }

  private roundToSix(num: number): number {
    return +(Math.round(Number(num + 'e+6')) + 'e-6');
  }
}
